// ============================================================================
// PairsForm — Formatage partagé (dates, montants, durées, noms, modalités)
// ============================================================================
// Lot 6.5 du refactoring (2026-10-17) : UN SEUL jeu de formateurs pour le
// serveur (emails, documents, exports) et le client (écrans). Avant : 18
// copies de fmtDate, 17 de fmtEur, 5 d'initiales, 3 libellés de modalité
// (dont « Presentiel » sans accent dans les contrats), 71 « ${prenom} ${nom} »
// recopiés côté serveur. Lot 7.1 : les écrans aussi (262 `toLocale*` inline,
// 41 helpers locaux) — neuf formats courts ajoutés, importés directement, sans
// couche `lib/dates.ts` (décision FX).
//
// Règles :
//   - Fuseau : l'app raisonne en HEURE DE PARIS (tz.ts côté serveur). Un
//     horodatage (Date, ISO avec heure, timestamp) est toujours rendu en
//     Europe/Paris, quel que soit le fuseau du process ou du navigateur. Une
//     date SEULE (« 2026-03-02 », colonnes `date` de Postgres) est lue comme
//     un jour civil, jamais décalée : même rendu partout (un navigateur à
//     l'ouest de Greenwich affichait la veille avec `new Date("2026-03-02")`).
//   - Vide (null, "", NaN, date invalide) → `vide` (« — » par défaut), que
//     chaque appelant peut remplacer (documents v2 : chaîne vide, le moteur
//     marque lui-même les trous).
//   - Montants : Intl fr-FR, « 1 234,56 € » (espace fine insécable avant €).
// ============================================================================
export const VIDE = "—";
const TZ_PARIS = "Europe/Paris";
const DATE_SEULE = /^\d{4}-\d{2}-\d{2}$/;
function resoudreDate(v) {
    if (v === null || v === undefined || v === "")
        return null;
    if (typeof v === "string" && DATE_SEULE.test(v)) {
        const [annee, mois, jour] = v.split("-").map(Number);
        return { date: new Date(annee, mois - 1, jour), fuseau: undefined };
    }
    const date = v instanceof Date ? v : new Date(v);
    return isNaN(date.getTime()) ? null : { date, fuseau: TZ_PARIS };
}
function formaterDate(v, options, vide) {
    const r = resoudreDate(v);
    if (!r)
        return vide;
    return r.date.toLocaleDateString("fr-FR", { ...options, timeZone: r.fuseau });
}
/** « 02/03/2026 ». */
export function formatDateShortFR(v, vide = VIDE) {
    return formaterDate(v, { day: "2-digit", month: "2-digit", year: "numeric" }, vide);
}
/** « 2 mars 2026 ». */
export function formatDateFR(v, vide = VIDE) {
    return formaterDate(v, { day: "numeric", month: "long", year: "numeric" }, vide);
}
/** « lundi 2 mars 2026 ». */
export function formatDateJourFR(v, vide = VIDE) {
    return formaterDate(v, { weekday: "long", day: "numeric", month: "long", year: "numeric" }, vide);
}
/** « mars 2026 ». */
export function formatMoisAnneeFR(v, vide = VIDE) {
    return formaterDate(v, { month: "long", year: "numeric" }, vide);
}
/** « 02/03/2026 14:05 » — heure de Paris. */
export function formatDateHeureFR(v, vide = VIDE) {
    const r = resoudreDate(v);
    if (!r)
        return vide;
    return r.date.toLocaleString("fr-FR", {
        day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        timeZone: r.fuseau,
    });
}
/** « 2 mars 2026 à 14:05 » — heure de Paris, en toutes lettres (horodatages de signature sur les contrats et conventions). */
export function formatDateHeureLongueFR(v, vide = VIDE) {
    const r = resoudreDate(v);
    if (!r)
        return vide;
    return r.date.toLocaleString("fr-FR", { dateStyle: "long", timeStyle: "short", timeZone: r.fuseau });
}
// ---- Formats courts des écrans (lot 7.1, 2026-10-17) ------------------------
// Le client recopiait trente variantes d'options Intl ; elles se ramènent à ces
// neuf formats, nommés par ce qu'ils montrent. Un écran choisit un format, il
// n'écrit plus d'options.
/** « 2 mars » — jour et mois en toutes lettres, sans année (fils, cartes). */
export function formatJourMoisFR(v, vide = VIDE) {
    return formaterDate(v, { day: "numeric", month: "long" }, vide);
}
/** « 2 mars », « 2 févr. » — mois abrégé (chips, créneaux, colonnes étroites). */
export function formatJourMoisCourtFR(v, vide = VIDE) {
    return formaterDate(v, { day: "numeric", month: "short" }, vide);
}
/** « 02/03 » — en-têtes de matrice (émargements), là où chaque caractère compte. */
export function formatJJMMFR(v, vide = VIDE) {
    return formaterDate(v, { day: "2-digit", month: "2-digit" }, vide);
}
/** « lundi 2 mars » — jour de la semaine, sans année (séance à venir, événement). */
export function formatJourSemaineFR(v, vide = VIDE) {
    return formaterDate(v, { weekday: "long", day: "numeric", month: "long" }, vide);
}
/** « lun. 2 mars » — jour de la semaine abrégé, sans année (listes de séances). */
export function formatJourSemaineCourtFR(v, vide = VIDE) {
    return formaterDate(v, { weekday: "short", day: "numeric", month: "short" }, vide);
}
/** « lun. 2 mars 2026 » — jour de la semaine abrégé, avec année (créneaux de sondage). */
export function formatDateJourCourtFR(v, vide = VIDE) {
    return formaterDate(v, { weekday: "short", day: "numeric", month: "short", year: "numeric" }, vide);
}
const HEURE_SEULE = /^(\d{2}):(\d{2})/;
/**
 * « 14:05 » — heure de Paris d'un horodatage, ou les cinq premiers caractères
 * d'une heure seule « HH:MM[:SS] » (colonnes `heure_debut` / `heure_fin`).
 */
export function formatHeureFR(v, vide = VIDE) {
    if (typeof v === "string") {
        const m = HEURE_SEULE.exec(v);
        if (m)
            return `${m[1]}:${m[2]}`;
    }
    const r = resoudreDate(v);
    if (!r)
        return vide;
    return r.date.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit", timeZone: r.fuseau });
}
/** « 2 mars 14:05 » — horodatage compact des fils (notifications, actions, notes). */
export function formatJourMoisHeureFR(v, vide = VIDE) {
    const r = resoudreDate(v);
    if (!r)
        return vide;
    return r.date.toLocaleString("fr-FR", {
        day: "numeric", month: "short", hour: "2-digit", minute: "2-digit", timeZone: r.fuseau,
    });
}
/** « 1 234,5 » — nombre en typographie française, décimales fixées (0 par défaut). */
export function formatNombreFR(v, decimales = 0, vide = VIDE) {
    if (v === null || v === undefined || v === "")
        return vide;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n))
        return vide;
    return n.toLocaleString("fr-FR", { minimumFractionDigits: decimales, maximumFractionDigits: decimales });
}
/** « 1 234,56 € » ; null, chaîne vide ou non numérique → `vide`. */
export function formatEuros(v, opts = {}) {
    const vide = opts.vide ?? VIDE;
    if (v === null || v === undefined || v === "")
        return vide;
    const n = typeof v === "number" ? v : Number(v);
    if (!Number.isFinite(n))
        return vide;
    const decimales = opts.decimales ?? 2;
    const fraction = decimales === "auto" ? (Number.isInteger(n) ? 0 : 2) : decimales;
    return new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: fraction,
        maximumFractionDigits: fraction,
    }).format(n);
}
/** « 3 h », « 3.5 h » — durée en heures décimales (documents Inter). */
export function formatHeuresDecimales(minutes, vide = "") {
    if (!minutes || minutes <= 0)
        return vide;
    const h = minutes / 60;
    return `${h.toFixed(h % 1 === 0 ? 0 : 1)} h`;
}
/** « 3 h », « 3 h 30 » — durée en heures et minutes (contrats, conventions). */
export function formatHeuresMinutes(minutes, vide = "") {
    if (!minutes || minutes <= 0)
        return vide;
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
}
/** Libellé d'une modalité de formation (`groupes.modalite`) ; valeur inconnue rendue telle quelle. */
export function formatModalite(m, vide = "") {
    switch (m) {
        case "PRESENTIEL": return "Présentiel";
        case "DISTANCIEL": return "Distanciel";
        case "HYBRIDE": return "Hybride";
        default: return m ?? vide;
    }
}
/** « Prénom Nom », sans espace parasite quand un champ manque. */
export function nomCompletPersonne(prenom, nom) {
    return [prenom, nom]
        .map((s) => (s ?? "").trim())
        .filter(Boolean)
        .join(" ");
}
/** « JM » — initiales en majuscules ; `vide` (« ? ») si la fiche n'a ni prénom ni nom. */
export function initialesPersonne(prenom, nom, vide = "?") {
    const i = `${(prenom ?? "").trim().charAt(0)}${(nom ?? "").trim().charAt(0)}`.toUpperCase();
    return i || vide;
}
/** Libellés des statuts d'émargement (`statut_presence`) sur les pièces (feuille d'émargement). */
/** Statuts d'émargement qui VALENT présence — assiette unique de l'assiduité
 *  (attestation de fin de formation Inter) et des heures de supervision
 *  (lot K, 2026-10-27). Le retard et le départ anticipé comptent : pas de
 *  prorata (FX). */
export const STATUTS_VALANT_PRESENCE = ["PRESENT", "PARTI", "RETARD", "DEPART_ANTICIPE"];
export const LIBELLES_STATUT_PRESENCE = {
    PRESENT: "Présent",
    RETARD: "Présent (retard)",
    PARTI: "Présent",
    DEPART_ANTICIPE: "Présent (départ anticipé)",
    ABSENT_EXCUSE: "Absent excusé",
    ABSENT_NON_EXCUSE: "Absent",
    ATTENDU: "Non émargé",
    ENVOYE: "Non émargé",
    PLANIFIE: "Non émargé",
};
export function libelleStatutPresence(statut, vide = "Non émargé") {
    return (statut && LIBELLES_STATUT_PRESENCE[statut]) || vide;
}
/** Lot J (0293) : « 2 dernières séances offertes », « Dernière séance offerte »,
 *  « Cycle offert » quand N couvre le cycle ; chaîne vide si N ≤ 0. */
export function libelleSeancesOffertesFin(n, cycle) {
    const nb = Math.max(0, Math.trunc(n ?? 0));
    if (nb === 0)
        return "";
    if (cycle != null && cycle > 0 && nb >= cycle)
        return "Cycle offert";
    return nb === 1 ? "Dernière séance offerte" : `${nb} dernières séances offertes`;
}
//# sourceMappingURL=format.js.map